import React, { useState, useEffect, useRef } from 'react';
import { ArrowUpRight16 } from '@carbon/icons-react';
import dayjs from 'dayjs';
import { isEmpty, isNil } from 'lodash';

import {
  getEmailAttachment,
  notificationTypeDescription,
  isValidRegex,
  checkFileExtensionRegex
} from 'utilities/helper.util';
import { InlineLoadingStatus } from 'carbon-components-react/lib/components/InlineLoading/InlineLoading';

import { MainContainer } from 'layouts/components';
import { ReferralEnquiryInformation } from 'components';
import TextWithLinks from 'components/TextWithLinks';
import { useSelector, useDispatch } from 'react-redux';

import { selectUser } from 'redux/user/selector';
import { selectedReferral, selectReferralLoading } from 'redux/referrals';
import { selectLodgeEnquiry } from 'redux/enquiries';
import { selectEnquiryLoader } from 'redux/enquiries/selectors';
import { selectScreenSize } from 'redux/session/selectors';
import { fetchEnquiryShareLinkByIdService } from 'services/enquiry.service';
import {
  selectEmailPreviewing,
  selectEmailPreview,
  previewResponseStart
} from 'redux/emails';
import { connectWebsocketForReferral } from 'redux/referrals';
import {
  resetAttachmentPreviewUrl,
  resetAttachmentDownloadUrl
} from 'redux/enquiries/actions';

import {
  getJobStatusCodeMsg,
  getLocationTypesCodeMsg,
  getActivityTypesCodeMsg
} from 'appConstants/enquiry';

import Tools from './Tools';
import EmptyState from './EmptyState';
import ResponsesList from './ResponsesList';
import ResponsesContent from './ResponsesContent';
import PreviewerSuperModal from './PreviewerSuperModal';

export const ReferralDetailsView: React.FC<Props | any> = (props: Props) => {
  const dispatch = useDispatch();
  const { isModal = false, onClickClose } = props;
  const bodyDivRef = useRef<HTMLDivElement>(null);
  const isLoading: any = useSelector(selectEnquiryLoader);
  const isReferralLoading: any = useSelector(selectReferralLoading);
  const referral: any = useSelector(selectedReferral);
  const lodgeEnquiry: any = useSelector(selectLodgeEnquiry);
  const screenSize: any = useSelector(selectScreenSize);
  const [selectedReferralObj, setSelectedReferralObj] = useState<any>(null);
  const [selectedResponseObj, setSelectedResponseObj] = useState<any>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [enquiryValidityDate, setEnquiryValidityDate] = useState<string>('');
  const [enquiry, setEnquiry] = useState<any>();
  const [responses, setResponses] = useState<any>();
  const [safetyMessage, setSafetyMessage] = useState<string>('');
  const [headerTitle, setHeaderTitle] = useState<string>('Referral');
  const loadingEmailPreview: any = useSelector(selectEmailPreviewing);
  const emailPreview: any = useSelector(selectEmailPreview);
  const [initialSelectedViewItem, setInitialSelectedViewItem] = useState<any>(
    null
  );
  const [
    copyPageLinkState,
    setCopyPageLinkState
  ] = useState<InlineLoadingStatus>('inactive');
  const [connectedWebSocket, setConnectedWebSocket] = useState<boolean>(false);

  const user = useSelector(selectUser);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [selectedResponse, setselectedResponse] = useState<number>(-1);
  const [viewItem, setViewItem] = useState<Array<any>>([]);
  const [autoRenewStatus, setAutoRenewStatus] = useState<string>('');
  const [referralStatus, setReferralStatus] = useState<any>();
  const [inboundEmailId, setInboundEmailId] = useState<number>(0);
  const [
    showPreviewerSuperModal,
    setShowPreviewerSuperModal
  ] = useState<boolean>(false);

  const handleCopyPageLink = async () => {
    setCopyPageLinkState('active');

    if (typeof ClipboardItem && navigator.clipboard.write) {
      const text = new ClipboardItem({
        'text/plain': fetchEnquiryShareLinkByIdService(enquiry.id).then(
          (response) => {
            try {
              if (!response.error) {
                setCopyPageLinkState('finished');
              } else {
                setCopyPageLinkState('error');
              }

              return new Blob([response], { type: 'text/plain' });
            } catch (e) {
              console.error(e);
              setCopyPageLinkState('error');
              return new Blob([''], { type: 'text/plain' });
            } finally {
              setTimeout(() => setCopyPageLinkState('inactive'), 2000);
            }
          }
        )
      });

      text && navigator.clipboard.write([text]);
    } else {
      try {
        const response = await fetchEnquiryShareLinkByIdService(enquiry.id);

        if (!response.error) {
          navigator.clipboard.writeText(response);
          setCopyPageLinkState('finished');
        } else {
          setCopyPageLinkState('error');
        }
      } catch (e) {
        console.error(e);
        setCopyPageLinkState('error');
      } finally {
        setTimeout(() => setCopyPageLinkState('inactive'), 2000);
      }
    }
  };

  const resetAttachmentPreviewNDownload = () => {
    dispatch(resetAttachmentPreviewUrl());
    dispatch(resetAttachmentDownloadUrl());
  };

  const handleViewChange = (value: string) => {
    let id = '';
    let body = '';
    let type = '';
    let name = '';
    let isFileSupported = false;

    if (value === 'webMap') {
      type = value;
      body = selectedResponseObj?.custom_properties?.webMapURL;
      isFileSupported = true;
    } else if (value === 'responseBody') {
      body = emailPreview.body;
      type = 'html';
      name = 'responseBody';
      isFileSupported = true;
    } else {
      const valueSplit = value.split('|');
      if (valueSplit[0] === 'attachment') {
        const attachmentData = supportAttachment(valueSplit[1]);
        id = attachmentData?.data.id;
        body = attachmentData?.data.body;
        name = attachmentData?.data.name;
        type = attachmentData?.data.type;
        isFileSupported = attachmentData?.isFileSupported;
      } else if (valueSplit[0] === 'link') {
        const anchor = document.createElement('a');
        anchor.href = valueSplit[1];
        anchor.target = '_blank';
        anchor.click();
        isFileSupported = false;
        body = valueSplit[1];
        type = 'link';
        name = valueSplit[2];
      }
    }

    const data = {
      id,
      body,
      type,
      name
    };

    if (isFileSupported) {
      resetAttachmentPreviewNDownload();
      setPreviewData(data);
    }

    if (!isFileSupported && type !== 'link') {
      getEmailAttachment({ url: body, attachmentName: name });
    }
  };

  const supportAttachment = (valueIndex: string) => {
    let id = '';
    let body = '';
    let type = '';
    let name = '';
    let isFileSupported = false;
    const filename = emailPreview.Attachments[Number(valueIndex)].name;
    const mimeType =
      emailPreview.Attachments[Number(valueIndex)].mimeType || 'unknown';
    // check for file extension as pdf and gml sometimes having same mimetype
    if (
      ['application/pdf', 'application/octet-stream'].includes(mimeType) &&
      checkFileExtensionRegex(filename, 'pdf')
    ) {
      isFileSupported = true;
      type = 'pdf';
    } else if (
      [
        'image/png',
        'image/jpeg',
        'image/gif',
        'image/bmp',
        'image/vnd.microsoft.icon',
        'image/svg+xml',
        'image/tiff'
      ].includes(mimeType)
    ) {
      isFileSupported = true;
      type = 'image';
    } else {
      isFileSupported = false;
      type = 'unknown';
    }
    id = emailPreview.Attachments[Number(valueIndex)].id;
    body = emailPreview.Attachments[Number(valueIndex)].url;
    name = emailPreview.Attachments[Number(valueIndex)].name;
    const data = {
      id,
      body,
      type,
      name
    };

    return {
      data,
      isFileSupported
    };
  };

  const handleResponseSelection = (index: number) => {
    if (index + 1 === selectedResponse) return;
    // Guard against incomplete referral data
    if (!referral || !referral.Organisation) return;

    setselectedResponse(index + 1);
    setSelectedReferralObj({
      ...referral.Organisation,
      status: referral.status,
      externalId: referral.externalId
    });
    const enquiryValidUntil = dayjs(referral.enquiryCreatedAt).add(28, 'day');
    setEnquiryValidityDate(enquiryValidUntil.format('DD MMM YYYY'));
    setSelectedResponseObj(responses?.[index]);
    setInboundEmailId(responses?.[index]?.InboundEmail?.id);
    setPreviewData(null);
    setViewItem([]);
    setInitialSelectedViewItem(null);
  };

  const handleExpandClick = () => {
    setShowPreviewerSuperModal(!showPreviewerSuperModal);
  };

  const onClosePreviewerSuperModal = () => {
    setShowPreviewerSuperModal(false);
  };

  const combineNCleanWorkplaceLocationArray = (
    array1: string[],
    array2: string[]
  ) => {
    const combinedArray = array1.concat(array2);
    const filteredArray = combinedArray.filter(
      (item) => item !== 'Road reserve'
    );
    return filteredArray;
  };

  useEffect(() => {
    if (referral && selectedResponseObj?.id) {
      dispatch(
        previewResponseStart({
          responseId: selectedResponseObj.id
        })
      );
      const safetyMsg = isNil(
        selectedResponseObj?.custom_properties?.safetyMessage
      )
        ? referral?.Organisation?.safetyMessage
        : selectedResponseObj?.custom_properties?.safetyMessage;
      setSafetyMessage(safetyMsg || '');
    }
  }, [referral, selectedResponseObj, dispatch]);

  useEffect(() => {
    if (selectedReferralObj?.responses?.length > 0) {
      setSelectedResponseObj(selectedReferralObj.responses[0]);
      setInboundEmailId(selectedReferralObj.responses[0]?.InboundEmail?.id);
    }
  }, [selectedReferralObj]);

  useEffect(() => {
    if (!loadingEmailPreview && emailPreview) {
      const items = [];
      let selectedItem = null;
      let hasWebMap = false;
      let defaultWebMap = true;
      let webMap = null;

      const emailBody = {
        label: 'Response body',
        value: 'responseBody'
      };

      if (selectedResponseObj?.custom_properties?.webMapURL) {
        webMap = { label: 'Web map', value: 'webMap' };
        items.push(webMap);
        hasWebMap = true;
      }

      if (selectedResponseObj?.custom_properties?.links?.length > 0) {
        for (const link of selectedResponseObj.custom_properties.links) {
          const linkObj = {
            label: (
              <div className="attachment-dropdown-items">
                <div>{link.displayText}</div>
                <div>
                  <ArrowUpRight16 />
                </div>
              </div>
            ),
            value: `link|${link.url}|${link.displayText}`
          };
          items.push(linkObj);
        }
      }

      items.push(emailBody);
      for (const [index, attachment] of emailPreview.Attachments.entries()) {
        if (attachment.emailContentDisposition !== 'inline') {
          const attachmentObj = {
            label: attachment.name,
            value: `attachment|${index}`
          };
          items.push(attachmentObj);
        }
      }

      if (referral?.ReferralConfiguration?.primaryContent) {
        if (
          referral?.ReferralConfiguration?.primaryContent.responseType ===
            'response_attachment' ||
          !isEmpty(
            referral?.ReferralConfiguration?.primaryContent.filenameOption
          )
        ) {
          const filenameOption =
            referral.ReferralConfiguration.primaryContent.filenameOption;
          const filenameValue =
            referral.ReferralConfiguration.primaryContent.filenameValue;
          const filterItem = items.filter(
            (item) => item.value !== 'webMap' && item.value !== 'responseBody'
          );
          switch (filenameOption) {
            case 'start_with':
              selectedItem = filterItem.find((item) =>
                item['label']
                  .toLowerCase()
                  .startsWith(filenameValue.toLowerCase())
              );
              break;
            case 'end_with':
              selectedItem = filterItem.find((item) =>
                item['label'].toLowerCase().endsWith(filenameValue)
              );
              break;
            case 'contains':
              selectedItem = filterItem.find((item) =>
                item['label']
                  .toLowerCase()
                  .includes(filenameValue.toLowerCase())
              );
              break;
            default: {
              const isValid = isValidRegex(filenameValue);
              if (isValid) {
                const regex = new RegExp(filenameValue);
                selectedItem = filterItem.find((item) =>
                  regex.test(item['label'])
                );
              }
              break;
            }
          }
        }
        defaultWebMap = referral?.ReferralConfiguration?.primaryContent.webMap;
      }

      setViewItem(items);

      // Set the default view here
      // Set to Response Body until the AO settings page is done
      let data = {};
      if (hasWebMap && defaultWebMap) {
        data = {
          body: selectedResponseObj?.custom_properties?.webMapURL,
          type: 'webMap'
        };
      } else if (!isNil(selectedItem)) {
        const valueSplit = selectedItem.value.split('|');
        const support = supportAttachment(valueSplit[1]);
        if (support?.isFileSupported) {
          data = support?.data;
        }
      } else {
        data = {
          body: emailPreview.body,
          type: 'html'
        };
      }

      setInitialSelectedViewItem(
        hasWebMap && defaultWebMap
          ? webMap
          : !isNil(selectedItem)
          ? selectedItem
          : emailBody
      );
      setPreviewData(data);
    }
    // eslint-disable-next-line
  }, [loadingEmailPreview, emailPreview]);

  useEffect(() => {
    // Guard against incomplete data - Enquiry and its nested properties must exist
    if (!referral || !lodgeEnquiry || !referral.Enquiry) {
      return;
    }

    const { designAct, excavationAct } = lodgeEnquiry;
    const { Enquiry, Responses } = referral;

    // Additional guard for required Enquiry properties
    if (!Enquiry.locationTypes || !Enquiry.activityTypes) {
      return;
    }

    const {
      locationTypes,
      locationsInRoad,
      activityTypes,
      isPlanningJob
    } = Enquiry;
    const newLocationTypes: string[] = locationTypes.map((code: string) =>
      getLocationTypesCodeMsg(code)
    );

    let newActivityTypes: string[] = [];
    activityTypes.forEach((activity: string) => {
      if (activity.search(',') !== -1) {
        const activityArr = activity
          .split(',')
          .map((item: string) =>
            getActivityTypesCodeMsg(
              item.toUpperCase(),
              designAct,
              excavationAct,
              isPlanningJob
            )
          );
        newActivityTypes = [...newActivityTypes, ...activityArr];
      } else {
        newActivityTypes = [
          ...newActivityTypes,
          getActivityTypesCodeMsg(
            activity.toUpperCase(),
            designAct,
            excavationAct,
            isPlanningJob
          )
        ];
      }
    });

    const locationsInRoadCapilizeFirstLetter = locationsInRoad?.map(
      (word: any) => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
    );

    const workplaceLocationCombine = combineNCleanWorkplaceLocationArray(
      newLocationTypes,
      locationsInRoadCapilizeFirstLetter
    );

    const enquiry = {
      ...Enquiry,
      locationTypes: newLocationTypes.join(', '),
      locationsInRoad: locationsInRoadCapilizeFirstLetter.join(', '),
      workplaceLocation: workplaceLocationCombine.join(', '),
      activityTypes: newActivityTypes,
      notificationType: isEmpty(Responses)
        ? ''
        : notificationTypeDescription(Responses[0]?.notification_type)
    };

    if (Enquiry?.autoRenewUntil && !Enquiry?.isAutoRenewComplete) {
      setAutoRenewStatus('(On)');
    } else if (Enquiry?.isAutoRenewComplete) {
      setAutoRenewStatus('(Completed)');
    } else {
      setAutoRenewStatus('(Off)');
    }

    setReferralStatus(getJobStatusCodeMsg(referral.status));
    setEnquiry(enquiry);
    setResponses(Responses);
    setHeaderTitle(`Referral ${referral.externalId}`);
    // Use optional chaining to prevent crash when User is not loaded yet
    setIsOwner(Enquiry?.User?.id === user?.id);
  }, [referral, lodgeEnquiry, user]);

  useEffect(() => {
    if (!connectedWebSocket && referral?.id && referral?.Enquiry?.id) {
      dispatch(
        connectWebsocketForReferral({
          enquiryId: referral.Enquiry.id,
          referralId: referral.id
        })
      );

      setConnectedWebSocket(true);
    }
  }, [referral, connectedWebSocket, dispatch]);

  // Show loading state while referral data is being fetched
  const isDataLoading = isLoading || isReferralLoading;

  return (
    <div
      ref={bodyDivRef}
      id="referralDetailsView"
      data-testid="referralDetailsView"
      className={`referralDetailsView--wrapper container--wrapper referralDetailsView-modal-style`}
    >
      <MainContainer>
        <div className="main-wrapper">
          <div className={`referral-information-wrapper-modal`}>
            <ReferralEnquiryInformation
              enquiryDetail={enquiry}
              referralStatus={referralStatus}
              headerTitle={headerTitle}
              screenSize={screenSize}
              isLoading={isDataLoading}
              isModal={isModal}
              onClickClose={onClickClose}
            />
          </div>

          <div id="getResponses" className="get-responses">
            <div className="get-responses-list-actions">
              <ResponsesList
                responses={responses}
                handleResponseSelection={handleResponseSelection}
                selectedResponse={selectedResponse - 1}
              />
              <Tools
                copyPageLinkState={copyPageLinkState}
                copyPageLinkHandler={handleCopyPageLink}
                showCopyPageLink={isOwner}
                autoRenewStatus={autoRenewStatus}
              />
              {safetyMessage && (
                <div
                  className={`get-responses-list-actions-safetyMsg-content-row completed selected`}
                >
                  <div
                    className={`get-responses-list-actions-safetyMsg-content-row-indicator indicator-selected`}
                  />
                  <div className="get-responses-list-actions-safetyMsg">
                    <TextWithLinks text={safetyMessage} />
                  </div>
                </div>
              )}
            </div>
            <div className="get-responses-actions">
              {selectedResponse < 0 && (
                <EmptyState
                  waitTime={
                    referral?.Organisation?.TypicalWaitTime?.details.P90
                  }
                  haveResponses={referral?.Responses?.length > 0}
                />
              )}
              {selectedResponse >= 0 && (
                <>
                  <ResponsesContent
                    selectedReferralObj={selectedReferralObj}
                    selectedResponseObj={selectedResponseObj}
                    enquiryValidityDate={enquiryValidityDate}
                    loadingEmailPreview={loadingEmailPreview}
                    viewItem={viewItem}
                    handleViewChange={handleViewChange}
                    previewData={previewData}
                    handleExpandClick={handleExpandClick}
                    initialSelectedViewItem={initialSelectedViewItem}
                    inboundEmailId={inboundEmailId}
                  />
                </>
              )}
              <div className="get-plans-responses-about">
                <div className="get-plans-responses-about-description">
                  <span className="get-plans-responses-about-description-title">
                    What you should know about responses and plans.
                  </span>
                  Plans are indicative only and property connections may not be
                  shown. All assets may not be included in the response
                  provided. You can engage a skilled locator to assist with the
                  location of assets. Check your spam/junk folders for missing
                  responses. Please follow safe excavation advice.
                </div>
              </div>
            </div>
          </div>
          {showPreviewerSuperModal && (
            <PreviewerSuperModal
              isOpen={showPreviewerSuperModal}
              onClose={onClosePreviewerSuperModal}
              previewData={previewData}
              selectedReferralObj={selectedReferralObj}
              isModal={isModal}
              inboundEmailId={inboundEmailId}
            />
          )}
        </div>
      </MainContainer>
    </div>
  );
};

type ownProps = {
  isModal: boolean;
  isDisplay: boolean;
  onClickClose?: any;
  creatorUserId: number;
};

type Props = ownProps;

ReferralDetailsView.displayName = 'ReferralDetailsView';

export default React.memo(ReferralDetailsView);
